import { API_CONFIG } from '../config/api';
import axios from 'axios';

class AdminService {
  // Admin authentication
  async adminLogin(credentials) {
    try {
      // Remove /api from BASE_URL for admin endpoints - use regex to only replace /api at the end
      const adminBaseUrl = API_CONFIG.BASE_URL.replace(/\/api$/, '');
      const response = await axios.post(`${adminBaseUrl}/apiadmin/token/`, {
        username: credentials.username,
        password: credentials.password,
      });

      if (response.data.access) {
        localStorage.setItem('admin_token', response.data.access);
        localStorage.setItem('admin_refresh_token', response.data.refresh);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user || {}));
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Check if user is admin
  isAdmin() {
    // Authorization is decided by the backend staff check, never a browser allowlist.
    return Boolean(this.getAdminToken());
  }

  // Get admin token
  getAdminToken() {
    return localStorage.getItem('admin_token');
  }

  // Admin logout
  adminLogout() {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
  }

  // Create admin API instance
  getAdminApi() {
    const adminToken = this.getAdminToken();
    // Remove /api from BASE_URL for admin endpoints - use regex to only replace /api at the end
    const adminBaseUrl = API_CONFIG.BASE_URL.replace(/\/api$/, '');
    const adminApi = axios.create({
      baseURL: adminBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`,
      }
    });
    adminApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        const request = error.config;
        const refresh = localStorage.getItem('admin_refresh_token');
        if (error.response?.status === 401 && refresh && request && !request._adminRetry) {
          request._adminRetry = true;
          try {
            if (!this.refreshPromise) {
              this.refreshPromise = axios.post(`${adminBaseUrl}/apiadmin/refresh/`, { refresh })
                .then(({ data }) => {
                  localStorage.setItem('admin_token', data.access);
                  if (data.refresh) localStorage.setItem('admin_refresh_token', data.refresh);
                  return data.access;
                })
                .finally(() => { this.refreshPromise = null; });
            }
            request.headers.Authorization = `Bearer ${await this.refreshPromise}`;
            return adminApi(request);
          } catch (refreshError) {
            this.adminLogout();
            window.dispatchEvent(new Event('admin-session-expired'));
            throw refreshError;
          }
        }
        throw error;
      },
    );
    return adminApi;
  }

  // ===== USER MANAGEMENT =====
  
  // Get all users
  async getUsers(params = {}) {
    try {
      const adminToken = this.getAdminToken();
      if (!adminToken) {
        throw new Error('Admin token not found. Please log in as admin.');
      }
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50, // Default limit to get more users
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/admin_rest/exchangeuser/', { params: defaultParams });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Admin session expired. Please log in again.');
      }
      throw error.response?.data || error.message;
    }
  }

  // Search users
  async searchUsers(searchTerm, params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters for search
      const defaultParams = {
        search: searchTerm,
        limit: 50, // Default limit for search results
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/admin_rest/exchangeuser/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Top up user balance
  async topupUser(userIds, amount, currency, password) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/admin_rest/exchangeuser/topup/', {
        ids: userIds,
        amount: amount.toString(),
        currency: currency,
        password: password
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get user balances
  async getUserBalances(userId, userEmail) {
    try {
      const adminApi = this.getAdminApi();
      // Use email for search as it works better with the admin API
      const searchParam = userEmail || userId;
      const response = await adminApi.get(`/apiadmin/admin_rest/balance/?search=${searchParam}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get user details
  async getUserDetails(userId) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/admin_rest/exchangeuser/${userId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async updateUser(userId, changes) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/admin_rest/exchangeuser/${userId}/`, changes);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== COINS & PAIRS MANAGEMENT =====

  // Get coins data from public API
  async getCoinsData() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/v1/coins/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get coins status
  async getCoinsStatus() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/v1/getcoinsstatus/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async getLimits() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/v1/limits/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get withdrawal limit levels
  async getWithdrawalLimitLevels(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/core/withdrawallimitlevel/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create withdrawal limit level
  async createWithdrawalLimitLevel(data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/withdrawallimitlevel/', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update withdrawal limit level
  async updateWithdrawalLimitLevel(id, data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/withdrawallimitlevel/${id}/`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Delete withdrawal limit level
  async deleteWithdrawalLimitLevel(id) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.delete(`/apiadmin/core/withdrawallimitlevel/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get user exchange fees
  async getUserExchangeFees(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/core/userexchangefee/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create user exchange fee
  async createUserExchangeFee(data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/userexchangefee/', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update user exchange fee
  async updateUserExchangeFee(id, data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/userexchangefee/${id}/`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Delete user exchange fee
  async deleteUserExchangeFee(id) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.delete(`/apiadmin/core/userexchangefee/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get withdrawal fees
  async getWithdrawalFees(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/core/withdrawalfee/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create withdrawal fee
  async createWithdrawalFee(data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/withdrawalfee/', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update withdrawal fee
  async updateWithdrawalFee(id, data) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/withdrawalfee/${id}/`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Delete withdrawal fee
  async deleteWithdrawalFee(id) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.delete(`/apiadmin/core/withdrawalfee/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== KEEPER MANAGEMENT APIs =====

  // Get keeper details
  async getKeeperDetails(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/details/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update keeper wallet
  async updateKeeperWallet(currency, address, privateKey) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/keeper/update-wallet/`, {
        currency,
        address,
        private_key: privateKey
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get keeper balance
  async getKeeperBalance(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/balance/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get keeper accumulation status
  async getKeeperAccumulationStatus(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/accumulation-status/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Trigger keeper accumulation
  async triggerKeeperAccumulation(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/keeper/accumulate/`, {
        currency
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Check keeper wallet addresses
  async checkKeeperWalletAddresses(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/wallet-addresses/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== DEPOSIT MANAGEMENT APIs =====

  // Approve all pending deposits
  async approveAllPendingDeposits(currency = null) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/deposits/approve-all/`, {
        currency
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Inspect deposit transactions
  async inspectDepositTransactions(currency, address = null) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/deposits/inspect/`, {
        params: { currency, address }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Check deposit accumulation
  async checkDepositAccumulation(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/deposits/accumulation-status/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Fix deposit accumulation
  async fixDepositAccumulation(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/deposits/fix-accumulation/`, {
        currency
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== WITHDRAWAL MANAGEMENT APIs =====

  // Process BTC1 payouts
  async processBTC1Payouts() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/withdrawals/process-btc1-payouts/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Inspect withdrawals
  async inspectWithdrawals(currency, state = null) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/withdrawals/inspect/`, {
        params: { currency, state }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update withdrawal limits
  async updateWithdrawalLimits(level, amount) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/withdrawals/update-limits/`, {
        level,
        amount
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== QUICK SWAP MANAGEMENT APIs =====

  // Add quick swap liquidity
  async addQuickSwapLiquidity(currency, amount, type) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/quick-swap/add-liquidity/`, {
        currency,
        amount,
        type
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Remove quick swap liquidity
  async removeQuickSwapLiquidity(currency, amount, type) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/quick-swap/remove-liquidity/`, {
        currency,
        amount,
        type
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get quick swap status
  async getQuickSwapStatus(currency = 'ALL') {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/quick-swap/status/`, {
        params: { currency }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }


  // ===== SYSTEM MANAGEMENT APIs =====

  // Seed limits
  async seedLimits() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/system/seed-limits/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Check system status
  async getSystemStatus() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/system/status/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Check accumulation limits
  async checkAccumulationLimits(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/system/accumulation-limits/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Fix accumulation limits
  async fixAccumulationLimits(currency, limit) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post(`/apiadmin/system/fix-accumulation-limits/`, {
        currency,
        limit
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get wallet transactions (deposits/withdrawals) with pagination
  async getWalletTransactions(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters
      const defaultParams = {
        limit: 50,
        offset: 0,
        ...params
      };
      
      // Calculate offset from page and pageSize if provided
      if (params.page && params.pageSize) {
        defaultParams.offset = (params.page - 1) * params.pageSize;
        defaultParams.limit = params.pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/core/wallettransactions/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Force deposit and accumulate
  async forceDepositAndAccumulate(transactionIds) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/wallettransactions/force_deposit_and_accumulate/', {
        action: 'force_deposit_and_accumulate',
        selected_items: transactionIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Revert transactions
  async revertTransactions(transactionIds) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/wallettransactions/revert/', {
        action: 'revert',
        selected_items: transactionIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Recheck KYT scoring
  async recheckKYT(transactionIds) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/wallettransactions/recheck_kyt/', {
        action: 'recheck_kyt',
        selected_items: transactionIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Set external accumulation address
  async setExternalAccumulation(transactionIds, externalAddress) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/wallettransactions/external_accumulation/', {
        action: 'external_accumulation',
        selected_items: transactionIds,
        external_address: externalAddress
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get pending transactions summary
  async getPendingTransactionsSummary() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/v1/monitor/pending-summary/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Trigger deposit check
  async triggerDepositCheck(currency = null, userId = null) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/monitor/trigger-deposit/`, {
        currency,
        user_id: userId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get disabled coins with pagination
  async getDisabledCoins(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50,
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/core/disabledcoin/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update disabled coin settings
  async updateDisabledCoin(coinId, settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/disabledcoin/${coinId}/`, settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create disabled coin entry
  async createDisabledCoin(settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/disabledcoin/', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get pairs with pagination
  async getPairs(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50,
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/core/pair/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create pair
  async createPair(pairData) {
    try {
      const adminApi = this.getAdminApi();
      // Transform field names to match backend expectations
      const transformedData = {
        base: pairData.base_currency || pairData.base,
        quote: pairData.quote_currency || pairData.quote,
        is_enabled: pairData.is_enabled
      };
      const response = await adminApi.post('/apiadmin/core/pair/', transformedData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get pair settings with pagination
  async getPairSettings(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50,
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/core/pairsettings/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update pair settings
  async updatePairSettings(settingsId, settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/pairsettings/${settingsId}/`, settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create pair settings
  async createPairSettings(settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/core/pairsettings/', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== TRADING & ORDERS =====

  // Get all orders
  async getAllOrders(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50, // Default limit to get more orders
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/admin_rest/allorder/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Cancel order
  async cancelOrder(orderId) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/admin_rest/allorder/cancel_order/', {
        id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Revert orders
  async revertOrders(orderIds) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/admin_rest/allorder/revert_orders/', {
        ids: orderIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get recent trades
  async getRecentTrades(pair = null) {
    try {
      const adminApi = this.getAdminApi();
      const url = pair ? `/api/v1/recent_trades/?pair=${pair}` : '/api/v1/recent_trades/';
      const response = await adminApi.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== WITHDRAWALS & DEPOSITS =====

  // Get withdrawal requests
  async getWithdrawalRequests(params = {}) {
    try {
      const adminApi = this.getAdminApi();
      
      // Set default pagination parameters if not provided
      const defaultParams = {
        limit: 50, // Default limit to get more withdrawals
        offset: 0,
        ...params
      };
      
      // If page is provided, calculate offset
      if (params.page && !params.offset) {
        const pageSize = params.pageSize || 20;
        defaultParams.offset = (params.page - 1) * pageSize;
        defaultParams.limit = pageSize;
      }
      
      const response = await adminApi.get('/apiadmin/admin_rest/withdrawalrequest/', { params: defaultParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Approve withdrawal
  async approveWithdrawal(withdrawalId) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/admin_rest/withdrawalrequest/approve/', {
        id: withdrawalId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Cancel withdrawal
  async cancelWithdrawal(withdrawalId) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/admin_rest/withdrawalrequest/cancel_withdrawal_request/', {
        id: withdrawalId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== SYSTEM SETTINGS =====

  // Get fees and limits
  async getFeesAndLimits() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/core/feesandlimits/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update fees and limits
  async updateFeesAndLimits(limitId, settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/core/feesandlimits/${limitId}/`, settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== BOT MANAGEMENT =====

  // Get bot configurations
  async getBotConfigs() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/bots/botconfig/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update bot configuration
  async updateBotConfig(botId, config) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/bots/botconfig/${botId}/`, config);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create bot configuration
  async createBotConfig(config) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/bots/botconfig/', config);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== STATISTICS & MONITORING =====

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/dashboard_rest/commonusersstats/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get trade volume stats
  async getTradeVolumeStats() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/dashboard_rest/tradevolume/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get common inouts stats
  async getCommonInoutsStats() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/dashboard_rest/commoninouts/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get deposits/withdrawals stats
  async getDepositsWithdrawalsStats() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/cryptocoins/depositswithdrawalsstats/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== NOTIFICATIONS =====

  // Get notifications
  async getNotifications() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/notifications/notification/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Create notification
  async createNotification(notification) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/notifications/notification/', notification);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== QUICK SWAP MANAGEMENT =====

  // Get quick swap liquidity status
  async getQuickSwapLiquidityStatus(currency = 'ALL') {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/quick-swap/status/', { params: { currency } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== SYSTEM COMMANDS =====

  // ===== KEEPER MANAGEMENT =====

  // Get all keepers
  async getKeepers() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/cryptocoins/keeper/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }


  // Update keeper settings
  async updateKeeper(keeperId, settings) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.patch(`/apiadmin/cryptocoins/keeper/${keeperId}/`, settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
  // Accumulate ALL user wallet funds to keeper
  async accumulateAllToKeeper(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/keeper/accumulate-all/', {
        currency
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Withdraw from keeper to external address
  async withdrawFromKeeper(currency, toAddress, amount) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/keeper/withdraw/', {
        currency,
        to_address: toAddress,
        amount
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get keeper accumulation limits
  async getKeeperAccumulationLimits(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/system/accumulation-limits/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update keeper accumulation limits
  async updateKeeperAccumulationLimits(currency, limits) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/system/fix-accumulation-limits/', {
        currency,
        limit: limits?.limit ?? limits?.limits_keeper_accumulation_balance ?? limits,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== ADDITIONAL MANAGEMENT APIs =====
  async checkBTC1DepositStatus() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/deposits/check-btc1-status/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async fixBTC1DepositAccumulation() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/deposits/fix-btc1-accumulation/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async inspectWithdrawalsDetailed(currency = 'BTC1', state = null) {
    try {
      const adminApi = this.getAdminApi();
      const params = { currency };
      if (state !== null) params.state = state;
      const response = await adminApi.get('/apiadmin/withdrawals/inspect-detailed/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async updateKeeperToNewWallet(currency, address, privateKey) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/keeper/update-to-new-wallet/', {
        currency,
        address,
        private_key: privateKey
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async updateKeeperToOldWallet(currency, address) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/keeper/update-to-old-wallet/', {
        currency,
        address
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async checkKeeperStatus(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/status/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async checkAllWalletsWithFunds(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/keeper/check-all-wallets/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async inspectAccumulationTransactions(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/accumulation/inspect-transactions/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async inspectDepositTransactionsDetailed(currency) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get(`/apiadmin/deposits/inspect-detailed/${currency}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== MISSING APIS =====

  async updateBTC1Price(price) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/quick-swap/update-btc1-price/', {
        price
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async clearSystemCache() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/system/clear-cache/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async restartWorkers() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/system/restart-workers/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async backupDatabase() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/system/backup-database/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async toggleMaintenanceMode(enabled) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/system/toggle-maintenance-mode/', {
        enabled
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // ===== ADDITIONAL MANAGEMENT COMMANDS =====

  async manageWithdrawalLimits(action, data = {}) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/withdrawals/manage-limits/', {
        action,
        ...data
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async manageQuickSwapLiquidity(action, currency, amount = null, type = 'liquidity') {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/quick-swap/manage-liquidity/', {
        action,
        currency,
        amount,
        type
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async disableUsersOrders(emails, disable = true) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/users/disable-orders/', {
        emails,
        disable
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async disableUsersWithdrawals(emails, disable = true) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/users/disable-withdrawals/', {
        emails,
        disable
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async disableUsersTopups(emails, disable = true) {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/users/disable-topups/', {
        emails,
        disable
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async getBTC1Price() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.get('/apiadmin/trading/get-btc1-price/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  async refreshOrderbook() {
    try {
      const adminApi = this.getAdminApi();
      const response = await adminApi.post('/apiadmin/trading/refresh-orderbook/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
}

const adminService = new AdminService();

export default adminService;
